import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaProjectDiagram, FaExternalLinkAlt, FaWallet, FaMoneyCheckAlt, FaBalanceScale } from 'react-icons/fa';
import { formatNumber } from 'helpers/utils';
import { Badge } from 'components/common';

// Small KPI card used in the summary strip.
const KpiCard = ({ icon: Icon, label, value, tone }) => (
  <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white">
    <span
      className="tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded tw-flex-shrink-0"
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      <Icon size={15} />
    </span>
    <div className="tw-min-w-0">
      <p className="tw-mb-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">{label}</p>
      <p className="tw-mb-0 tw-font-bold tw-text-sm tw-truncate" style={{ color: tone.fg }}>{value}</p>
    </div>
  </div>
);

KpiCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.object.isRequired,
};

const BookingJobTransactions = ({ booking }) => {
  const { job_transactions } = booking;
  const trips = job_transactions || [];

  // Shown in this trip's display currency (USD international / VND domestic),
  // all derived from the VND base so budget − total == remaining holds.
  const currency = booking?.project?.budget_currency || booking?.currency || 'VND';
  const totalExpense = `${formatNumber(booking.total_expense_by_job_display, currency)} ${currency}`;
  const budget = `${formatNumber(booking?.project?.budget_amount_display, currency)} ${currency}`;
  const remaining = `${formatNumber(booking?.project?.budget_remaining_display, currency)} ${currency}`;
  const remainingNegative = Number(booking?.project?.budget_remaining_display) < 0;

  if (!booking.job_number) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-text-center">
        <span className="tw-flex tw-items-center tw-justify-center tw-w-14 tw-h-14 tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 tw-mb-3">
          <FaProjectDiagram size={24} />
        </span>
        <h6 className="tw-mb-1 tw-font-semibold tw-text-slate-700">No job linked</h6>
        <p className="tw-mb-0 tw-text-sm tw-text-slate-400">This trip is not following a project budget, so there are no related job transactions.</p>
      </div>
    );
  }

  return (
    <div className="tw-p-4">
      {/* Header banner */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-4 tw-p-4 tw-rounded-lg tw-bg-gradient-to-r tw-from-slate-50 tw-to-blue-50 tw-border tw-border-solid tw-border-slate-200">
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded tw-bg-blue-100 tw-text-blue-600 tw-flex-shrink-0">
            <FaProjectDiagram size={18} />
          </span>
          <div>
            <p className="tw-mb-0 tw-text-xs tw-text-slate-500">Trips sharing job number</p>
            <p className="tw-mb-0 tw-font-bold tw-text-slate-800">{booking.job_number}</p>
          </div>
        </div>
        <Badge variant="primary" size="md">{trips.length} {trips.length === 1 ? 'trip' : 'trips'}</Badge>
      </div>

      {/* KPI summary strip */}
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-mb-4">
        <KpiCard icon={FaMoneyCheckAlt} label="Total expense" value={totalExpense} tone={{ bg: '#dbeafe', fg: '#2563eb' }} />
        <KpiCard icon={FaWallet} label={`Budget ${booking.job_number}`} value={budget} tone={{ bg: '#dcfce7', fg: '#16a34a' }} />
        <KpiCard icon={FaBalanceScale} label="Budget remaining" value={remaining} tone={remainingNegative ? { bg: '#fee2e2', fg: '#dc2626' } : { bg: '#f1f5f9', fg: '#475569' }} />
      </div>

      {/* Trips table */}
      <div className="tw-overflow-x-auto tw-border tw-border-solid tw-border-slate-200 tw-rounded-lg">
        <table className="tw-w-full tw-border-collapse tw-text-sm">
          <thead>
            <tr className="tw-bg-slate-50 tw-text-left tw-text-slate-500">
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold tw-text-xs tw-uppercase tw-tracking-wide">Trip ID#</th>
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold tw-text-xs tw-uppercase tw-tracking-wide">Trip name</th>
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold tw-text-xs tw-uppercase tw-tracking-wide">Travelers</th>
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold tw-text-xs tw-uppercase tw-tracking-wide tw-text-right">Expense</th>
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold tw-text-xs tw-uppercase tw-tracking-wide">Note</th>
            </tr>
          </thead>
          <tbody>
            {trips.map((trip) => {
              const isCurrent = trip.id === booking.id;
              return (
                <tr key={trip.id} className={`tw-border-t tw-border-solid tw-border-slate-100 ${isCurrent ? 'tw-bg-blue-50' : 'hover:tw-bg-slate-50'}`}>
                  <td className="tw-px-3 tw-py-2.5">
                    <Link to={`/booking/travel/${trip.id}`} target="_blank" className="tw-inline-flex tw-items-center tw-gap-1 tw-text-blue-600 tw-font-medium tw-no-underline hover:tw-underline">
                      {trip.id} <FaExternalLinkAlt size={9} />
                    </Link>
                  </td>
                  <td className="tw-px-3 tw-py-2.5 tw-text-slate-700">{trip.trip_name}</td>
                  <td className="tw-px-3 tw-py-2.5">
                    <span className="tw-flex tw-flex-wrap tw-gap-1">
                      {(trip.travelers || []).map((name) => <Badge key={name} variant="primary" size="sm">{name}</Badge>)}
                    </span>
                  </td>
                  <td className="tw-px-3 tw-py-2.5 tw-text-right tw-font-medium tw-text-slate-700">{formatNumber(trip.expense, currency)} {currency}</td>
                  <td className="tw-px-3 tw-py-2.5">
                    {isCurrent && <Badge variant="warning" size="sm">Current booking</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="tw-border-t tw-border-solid tw-border-slate-200 tw-bg-slate-50 tw-font-semibold">
              <td colSpan={3} className="tw-px-3 tw-py-2.5 tw-text-right tw-text-slate-600">Total expense</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-right tw-text-blue-700">{totalExpense}</td>
              <td />
            </tr>
            <tr className="tw-border-t tw-border-solid tw-border-slate-100 tw-bg-slate-50 tw-font-semibold">
              <td colSpan={3} className="tw-px-3 tw-py-2.5 tw-text-right tw-text-slate-600">Budget for {booking.job_number}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-right tw-text-green-700">{budget}</td>
              <td />
            </tr>
            <tr className="tw-border-t tw-border-solid tw-border-slate-100 tw-bg-slate-50 tw-font-semibold">
              <td colSpan={3} className="tw-px-3 tw-py-2.5 tw-text-right tw-text-slate-600">
                Budget remaining
                <p className="tw-text-slate-400 tw-text-xs tw-font-normal tw-mb-0">(after deducting travel expenses)</p>
              </td>
              <td className={`tw-px-3 tw-py-2.5 tw-text-right ${remainingNegative ? 'tw-text-red-600' : 'tw-text-slate-700'}`}>{remaining}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

BookingJobTransactions.propTypes = {
  booking: PropTypes.object.isRequired,
};

export default BookingJobTransactions;
